import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTokens } from '../context/TokenContext';
import { useXP } from '../context/XPContext';
import { useBugReport } from '../context/BugReportContext';
import { useHints } from '../hints';
import HelpCodex from './HelpCodex/HelpCodex';

import { startStripeCheckout } from '../utils/upgradeFlow';
import SettingsModal from './SettingsModal';
import FeatureRequestModal from './FeatureRequestModal';
import { GameTipPanel, GameTipHost } from './GameTip';
import './TopNav.css';


function TopNav({ hidden = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { tokens } = useTokens();
  const { xpStatus } = useXP();
  const { activeQuestion, openBugReport } = useBugReport();
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const dropdownRef = useRef(null);
  const { seenHints, discoveredCount, HINT_IDS } = useHints();
  const [showHelpCodex, setShowHelpCodex] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'partner_admin';
  const adminNavTarget = user?.role === 'partner_admin'
    ? '/partner-admin'
    : '/admin';
  const adminNavLabel = user?.role === 'partner_admin' ? 'Partner Admin' : 'Admin';
  const showAdminNav = isAdmin;
  // Topbar Dev Portal toggle — owner verifier surface for the role='admin'
  // account that sits OUTSIDE the floating partner_admin tile. Lets Shay open
  // the partner-trusted dev environment from his admin nav without colliding
  // with the admin shell UI. partner_admin users see the floating tile in
  // AdminLayout.jsx instead.
  const showDevPortalToggle = user?.role === 'admin';

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Auto-open settings modal when returning from Stripe payment portal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_updated') === 'success') {
      setShowSettingsModal(true);
    }
  }, [location.search]);

  // Listen for modal-open events (from LimitReachedModal, ExamTabBar, etc.)
  useEffect(() => {
    const onSettings = () => setShowSettingsModal(true);
    const onFeature = () => setShowFeatureModal(true);
    const onHelp = () => setShowHelpCodex(true);
    // Backwards-compat: callers that still dispatch open-pricing-modal route
    // straight to Stripe (when a tier is provided) or to /pricing.
    const onPricing = (e) => {
      const tier = e?.detail?.tier;
      if (tier) {
        startStripeCheckout(tier, { fallbackNavigate: navigate });
      } else {
        startStripeCheckout('annual', { fallbackNavigate: navigate });
      }
    };
    window.addEventListener('open-settings-modal', onSettings);
    window.addEventListener('open-feature-modal', onFeature);
    window.addEventListener('open-help-codex', onHelp);
    window.addEventListener('open-pricing-modal', onPricing);
    return () => {
      window.removeEventListener('open-settings-modal', onSettings);
      window.removeEventListener('open-feature-modal', onFeature);
      window.removeEventListener('open-help-codex', onHelp);
      window.removeEventListener('open-pricing-modal', onPricing);
    };
  }, [navigate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    navigate('/login');
  };

  return (
    <>
    <nav className={`top-nav${hidden ? ' top-nav--hidden' : ''}`}>
      <div className="top-nav-container">
        <div className="top-nav-left">
          <div className="logo-container">
            <Link to="/dashboard" className="logo-link">
              <span className="logo-bono">DEBONO</span>
              <img src="/favicon-32x32.png?v=7" alt="" className="logo-favicon" />
              <span className="logo-rx-pill">rx</span>
            </Link>
          </div>
          <button
            className={`hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>
        </div>

        {mobileMenuOpen && (
          <div
            className="mobile-menu-backdrop"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        <div className={`top-nav-center ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
          <Link
            to="/dashboard"
            className={`nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
          >
            Dashboard
          </Link>
          <Link
            to="/bank"
            className={`nav-link ${location.pathname.startsWith('/bank') ? 'active' : ''}`}
          >
            Bank
          </Link>
          <Link
            to="/train"
            className={`nav-link ${location.pathname === '/train' ? 'active' : ''}`}
          >
            Train
          </Link>
          <Link
            to="/ask"
            className={`nav-link ${location.pathname === '/ask' ? 'active' : ''}`}
          >
            Ask
          </Link>
          <Link
            to="/contents"
            className={`nav-link mobile-menu-only ${location.pathname === '/contents' ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            Table of Contents
          </Link>
          {(xpStatus?.level || 0) >= 10 ? (
            <Link
              to="/social"
              className={`nav-link ${location.pathname.startsWith('/social') ? 'active' : ''}`}
            >
              PvP
            </Link>
          ) : (
            <GameTipHost
              as="span"
              className="nav-link nav-link--locked combat-tt-host"
              tabIndex={0}
              aria-label="PvP locked, reach level 10 to unlock"
            >
              PvP
              <GameTipPanel
                placement="below-left"
                name="PvP"
                sections={[{ lines: [{ stats: [{ g: 'lock', v: 'level 10' }, { j: 'to' }, { t: 'unlock' }] }] }]}
              />
            </GameTipHost>
          )}
          {showAdminNav && (
            <Link
              to={adminNavTarget}
              className={`nav-link ${location.pathname.startsWith(adminNavTarget) ? 'active' : ''}`}
            >
              {adminNavLabel}
            </Link>
          )}

        </div>

        <div className="top-nav-right">
          {/* Only show subscription badge when user data is loaded */}
          {user && (
            user.subscription_tier === 'base' ? (
              <button
                onClick={() => startStripeCheckout('annual', { fallbackNavigate: navigate })}
                className="upgrade-button"
                aria-label="Upgrade to Premium"
              >
                Upgrade
              </button>
            ) : (
              <div className="premium-badge">
                Premium
              </div>
            )
          )}

          {/* Feature Guide (Help Codex) Button */}
          <button
            className="help-codex-nav-button"
            onClick={() => setShowHelpCodex(true)}
            aria-label="Feature guide"
          >
            <span style={{ fontSize: '15px', fontWeight: 700, color: 'inherit' }}>?</span>
          </button>

          {/* Dev Portal Toggle — owner verifier (Shay's role=admin account) */}
          {showDevPortalToggle && (
            <a
              href="http://[fd7a:115c:a1e0::1701:473f]:3050/"
              target="_blank"
              rel="noopener noreferrer"
              className="dev-portal-nav-button"
              aria-label="Open partner-trusted Debono dev environment"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="2,12 6,12 8,8 11,16 13,4 15,20 17,12 22,12" />
              </svg>
            </a>
          )}

          {/* Feature Request Button */}
          <button
            className="feature-request-nav-button"
            onClick={() => setShowFeatureModal(true)}
            aria-label="Request a feature"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>

          {/* Bug Report Button */}
          <button
            className="bug-report-nav-button"
            onClick={() => openBugReport(activeQuestion)}
            aria-label="Report a bug"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 2l1.88 1.88" />
              <path d="M14.12 3.88L16 2" />
              <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
              <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
              <path d="M12 20v-9" />
              <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
              <path d="M6 13H2" />
              <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
              <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
              <path d="M22 13h-4" />
              <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
            </svg>
          </button>


          <button
            className="settings-nav-button"
            onClick={() => setShowSettingsModal(true)}
            aria-label="Settings"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
          <div className="nav-actions" ref={dropdownRef}>
            <button
              className="profile-button"
              onClick={() => setShowDropdown(!showDropdown)}
              aria-label="User menu"
            >
              <div className="profile-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z" fill="currentColor"/>
                  <path d="M10 12C5.58172 12 2 13.7909 2 16V20H18V16C18 13.7909 14.4183 12 10 12Z" fill="currentColor"/>
                </svg>
              </div>
            </button>

            {showDropdown && (
              <div className="profile-dropdown">
                {user && (
                  <div className="dropdown-header">
                    <span className="dropdown-email">{user.email}</span>
                  </div>
                )}
                <div className="dropdown-divider" />
                <button className="dropdown-item logout-item" onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>


    <FeatureRequestModal
      isOpen={showFeatureModal}
      onClose={() => setShowFeatureModal(false)}
    />

    <SettingsModal
      isOpen={showSettingsModal}
      onClose={() => setShowSettingsModal(false)}
    />

    {showHelpCodex && (
      <HelpCodex
        seenHints={seenHints}
        discoveredCount={discoveredCount}
        totalHints={HINT_IDS.length}
        onClose={() => setShowHelpCodex(false)}
      />
    )}
    </>
  );
}

export default TopNav;
